import { isNumber, isUndefined } from 'lodash-es';

import { useHoneycombBaseScale } from './use-honeycomb-base-scale.js';
import { useHoneycombExtent } from './use-honeycomb-extent.js';
import { useHoneycombFormatterConfig } from './use-honeycomb-formatter-config.js';
import { useHoneycombTickFormatter } from './use-honeycomb-tick-formatter.js';
import { buildColorRule } from '../../core/color-rule/builder.js';
import { useColorRuleConfig } from '../../core/context/ColorRuleContext.js';
import { getValidColor } from '../../core/utils/getValidColor.js';
import { HoneycombTooltipMetadata } from '../types/honeycomb-internal.js';
import { HoneycombTileData } from '../types/honeycomb.js';
import { TileColorMetadata } from '../types/rendering.js';
import { getBaseColor } from '../utils/get-base-color.js';

export const useBuildMetadata = (data: HoneycombTileData[]) => {
  const scale = useHoneycombBaseScale();
  const extent = useHoneycombExtent();
  const honeycombDataWithColoringMetadata: WeakMap<
    HoneycombTileData,
    TileColorMetadata
  > = new WeakMap();

  const honeycombDataWithTooltipMetadata: WeakMap<
    HoneycombTileData,
    HoneycombTooltipMetadata
  > = new WeakMap();

  const { unit } = useHoneycombFormatterConfig();
  const formatter = useHoneycombTickFormatter();
  const colorRules = useColorRuleConfig();
  const ruleColor = buildColorRule(colorRules)(data, 'honeycomb');

  for (let i = 0; i < data.length; i++) {
    const index = i;
    const { value, name } = data[i];
    const baseColor = getBaseColor({ scale, value }, extent);
    const validColor =
      ruleColor.for(data[i])?.Default ?? getValidColor(baseColor as string);
    const tileColorData = {
      index,
      baseColor: validColor,
    };

    const honeycombTooltipMetadata: HoneycombTooltipMetadata = {
      id: `${name ?? 'unknown'}-${i}`,
      ...data[i],
      name: name ?? '',
      color: { Default: validColor },
    };

    if (isNumber(value) && !isUndefined(formatter)) {
      honeycombTooltipMetadata.value = formatter(value, unit);
    }

    honeycombDataWithColoringMetadata.set(data[i], tileColorData);
    honeycombDataWithTooltipMetadata.set(data[i], honeycombTooltipMetadata);
  }

  return {
    honeycombDataWithColoringMetadata,
    honeycombDataWithTooltipMetadata,
  };
};
